/**
 * Manager de links del proyecto.
 *
 * - Cada link muestra un icono con el color de la marca según `kind`
 * - Hover revela acciones: Preview (modal con iframe), abrir en pestaña, borrar
 * - Preview transforma la URL según la plataforma para soportar embed
 *   (Figma → /embed, Drive → /preview). Si la plataforma no permite
 *   iframe (CSP/X-Frame-Options) el modal incluye fallback de "Abrir
 *   en nueva pestaña".
 */
import { forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ExternalLink,
  Plus,
  Trash2,
  Eye,
  X,
  FileText,
  Globe,
  Pencil,
  Link as LinkIcon,
} from "lucide-react";
import {
  SiFigma,
  SiGithub,
  SiGitlab,
  SiGoogledrive,
  SiPlane,
  SiMural,
} from "@icons-pack/react-simple-icons";
import clsx from "clsx";
import { PROJECT_LINK_KINDS, projectLinkCreateSchema, type ProjectLink } from "@axorial/shared";
import { api } from "@/lib/api";

type Kind = (typeof PROJECT_LINK_KINDS)[number];

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;

interface KindMeta {
  label: string;
  icon: IconType;
  /** true: el SVG ya trae sus colores de marca; no necesita text-color en el wrapper.
   *  false: usa currentColor — aplicamos `color` y bg neutral. */
  branded: boolean;
  /** Para iconos branded: bg suave del wrapper. Para mono: text-color. */
  color: string;
  toEmbed?: (url: string) => string | null;
}

const KIND_META: Record<Kind, KindMeta> = {
  figma: {
    label: "Figma",
    icon: SiFigma,
    branded: true,
    color: "bg-base-200/70",
    toEmbed: (u) =>
      `https://www.figma.com/embed?embed_host=axorial-panel&url=${encodeURIComponent(u)}`,
  },
  drive: {
    label: "Drive",
    icon: SiGoogledrive,
    branded: true,
    color: "bg-base-200/70",
    toEmbed: (u) => {
      return u
        .replace(/\/edit\b[^?]*/, "/preview")
        .replace(/\/view\b[^?]*/, "/preview")
        .replace(/\?.*$/, "");
    },
  },
  plane: {
    label: "Plane",
    icon: SiPlane,
    branded: true,
    color: "bg-base-200/70",
  },
  github: {
    label: "GitHub",
    icon: SiGithub,
    branded: true,
    color: "bg-base-200/70",
    // GitHub no permite iframe — el preview cae al fallback de "abrir pestaña"
  },
  gitlab: {
    label: "GitLab",
    icon: SiGitlab,
    branded: true,
    color: "bg-base-200/70",
    // GitLab tampoco permite iframes por default.
  },
  mural: {
    label: "Mural",
    icon: SiMural,
    branded: true,
    color: "bg-base-200/70",
  },
  notes: {
    label: "Notas",
    icon: FileText,
    branded: false,
    color: "text-info",
  },
  client_site: {
    label: "Sitio del cliente",
    icon: Globe,
    branded: false,
    color: "text-emerald-400",
  },
  other: {
    label: "Otro",
    icon: LinkIcon,
    branded: false,
    color: "text-base-content/60",
  },
};

interface LinksManagerProps {
  projectId: string;
  links: ProjectLink[];
}

export function LinksManager({ projectId, links }: LinksManagerProps) {
  const addRef = useRef<HTMLDialogElement>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectLink | null>(null);
  const deleteRef = useRef<HTMLDialogElement>(null);
  const [previewLink, setPreviewLink] = useState<ProjectLink | null>(null);
  const [editingLink, setEditingLink] = useState<ProjectLink | null>(null);

  const openDelete = (link: ProjectLink) => {
    setPendingDelete(link);
    deleteRef.current?.showModal();
  };

  return (
    <>
      <div className="space-y-1.5">
        {links.length === 0 ? (
          <p className="text-sm text-base-content/55 italic">Sin links configurados aún.</p>
        ) : (
          <ul className="space-y-1 -mx-1">
            {links.map((l) => {
              const meta = KIND_META[l.kind] ?? KIND_META.other;
              const Icon = meta.icon;
              return (
                <li
                  key={l.id}
                  className="px-2 py-1.5 rounded-md hover:bg-base-200/60 group flex items-center gap-2 transition-colors"
                >
                  <span
                    className={clsx(
                      "size-7 rounded-md grid place-items-center shrink-0",
                      meta.branded ? "bg-base-200/70" : clsx("bg-base-200/70", meta.color),
                    )}
                    title={meta.label}
                    aria-hidden
                  >
                    <Icon className={meta.branded ? "size-4" : "size-3.5"} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium truncate block link-hover hover:text-primary transition-colors"
                      title={l.url}
                    >
                      {l.label}
                    </a>
                    <div className="text-[10px] uppercase tracking-wider text-base-content/45">
                      {meta.label}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Drive bloquea iframes — skip el botón aunque la fila
                        tenga hasPreview=true (data legacy). El external link
                        a la derecha cubre la apertura del archivo. */}
                    {l.hasPreview && !KINDS_WITHOUT_PREVIEW_SUPPORT.has(l.kind) && (
                      <button
                        type="button"
                        onClick={() => setPreviewLink(l)}
                        className="btn btn-xs btn-primary btn-outline gap-1 rounded-full px-3"
                        title="Vista previa en iframe"
                      >
                        <Eye className="size-3" /> Ver
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingLink(l)}
                      className="btn btn-ghost btn-xs btn-square text-base-content/55 hover:text-primary"
                      aria-label="Editar"
                      title="Editar link"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-ghost btn-xs btn-square text-base-content/55 hover:text-primary"
                      aria-label="Abrir"
                      title="Abrir en nueva pestaña"
                    >
                      <ExternalLink className="size-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => openDelete(l)}
                      className="btn btn-ghost btn-xs btn-square text-error/70 hover:text-error"
                      aria-label={`Eliminar link ${l.label}`}
                      title="Eliminar"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <button
          type="button"
          onClick={() => addRef.current?.showModal()}
          className="btn btn-ghost btn-sm w-full justify-start text-base-content/70 hover:text-primary"
        >
          <Plus className="size-3.5" /> Agregar link
        </button>
      </div>

      <AddLinkDialog ref={addRef} projectId={projectId} onClose={() => addRef.current?.close()} />

      <DeleteLinkDialog
        ref={deleteRef}
        projectId={projectId}
        link={pendingDelete}
        onClose={() => {
          deleteRef.current?.close();
          setPendingDelete(null);
        }}
      />

      {previewLink && <PreviewModal link={previewLink} onClose={() => setPreviewLink(null)} />}

      {editingLink && (
        <EditLinkDialog
          projectId={projectId}
          link={editingLink}
          onClose={() => setEditingLink(null)}
        />
      )}
    </>
  );
}

// ─── Preview con iframe ───────────────────────────────────────────────────

function PreviewModal({ link, onClose }: { link: ProjectLink; onClose: () => void }) {
  const meta = KIND_META[link.kind] ?? KIND_META.other;
  const Icon = meta.icon;
  const embedUrl = meta.toEmbed ? meta.toEmbed(link.url) : link.url;
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  // Detectar si el iframe nunca carga (CSP/X-Frame-Options) — 5s timeout.
  useEffect(() => {
    if (loaded) return;
    const t = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(t);
  }, [loaded]);

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-5xl w-full h-[85vh] p-0 flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-base-300/60 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={clsx(
                "size-7 rounded-md grid place-items-center shrink-0 bg-base-200/70",
                meta.color,
              )}
              aria-hidden
            >
              <Icon className="size-3.5" />
            </span>
            <div className="min-w-0">
              <div className="font-display font-semibold truncate">{link.label}</div>
              <div className="text-[10px] uppercase tracking-wider text-base-content/55">
                {meta.label}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-sm gap-1"
              title="Abrir en nueva pestaña"
            >
              <ExternalLink className="size-4" />
              <span className="hidden sm:inline">Abrir</span>
            </a>
            <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
              <X className="size-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 relative min-h-0 bg-base-200/40">
          <iframe
            src={embedUrl ?? link.url}
            title={`Preview ${link.label}`}
            className="absolute inset-0 w-full h-full"
            allow="fullscreen"
            onLoad={() => setLoaded(true)}
          />
          {!loaded && timedOut && (
            <div className="absolute inset-0 grid place-items-center bg-base-100/95 backdrop-blur-sm p-6">
              <div className="card-elevated max-w-md p-6 text-center space-y-3">
                <div className="size-12 rounded-full bg-warning/20 grid place-items-center mx-auto">
                  <ExternalLink className="size-5 text-warning" />
                </div>
                <h3 className="font-display font-semibold">No se puede previsualizar</h3>
                <p className="text-sm text-base-content/65">
                  {meta.label} bloquea la incrustación en iframes por seguridad. Abre en una pestaña
                  nueva para verlo.
                </p>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-sm"
                >
                  <ExternalLink className="size-4" /> Abrir en pestaña
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Add ──────────────────────────────────────────────────────────────────

/**
 * Kinds que típicamente SÍ soportan iframe embed (Figma, Drive). Otros
 * (GitHub, GitLab, mayoría de sitios) bloquean iframes vía CSP. Esta
 * lista alimenta el default del toggle "tiene vista previa" en el
 * dialog de creación — el usuario puede sobreescribir.
 */
// Google Drive bloquea su contenido en iframes (X-Frame-Options DENY),
// así que aunque el toggle exista, el preview no funciona — mejor
// abrir directo en pestaña con el botón externo. Por eso "drive" se
// excluye del default y de la UI de preview en la lista.
const KINDS_WITH_PREVIEW_DEFAULT: Kind[] = ["figma", "mural", "plane", "notes"];
const KINDS_WITHOUT_PREVIEW_SUPPORT: Set<Kind> = new Set(["drive"]);

const AddLinkDialog = forwardRef<HTMLDialogElement, { projectId: string; onClose: () => void }>(
  function AddLinkDialog({ projectId, onClose }, ref) {
    const qc = useQueryClient();
    const {
      register,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors },
    } = useForm({
      resolver: zodResolver(projectLinkCreateSchema),
      defaultValues: { kind: "other" as const, label: "", url: "", hasPreview: false },
    });

    // Cuando el usuario cambia de kind, sugerimos hasPreview según la
    // lista. Si toca manualmente el toggle después ya no se vuelve a
    // pisar — usamos useEffect no, pero al hacer onChange manual del
    // select sí lo seteamos. Implementación simple via subscribe.
    const watchedKind = watch("kind");
    useEffect(() => {
      setValue("hasPreview", KINDS_WITH_PREVIEW_DEFAULT.includes(watchedKind), {
        shouldDirty: false,
      });
    }, [watchedKind, setValue]);

    const create = useMutation({
      mutationFn: (input: { kind: string; label: string; url: string; hasPreview: boolean }) =>
        api.post(`/projects/${projectId}/links`, input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["project", projectId] });
        reset();
        onClose();
      },
    });

    return (
      <dialog ref={ref} className="modal">
        <div className="modal-box card-elevated">
          <h3 className="text-lg font-display font-semibold mb-4">Agregar link</h3>
          <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Tipo</span>
              </label>
              <select className="select select-bordered w-full" {...register("kind")}>
                {PROJECT_LINK_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_META[k].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Etiqueta</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="Ej. Mural de research"
                autoFocus
                {...register("label")}
              />
              {errors.label && (
                <span className="text-xs text-error mt-1 block">{errors.label.message}</span>
              )}
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">URL</span>
              </label>
              <input
                type="url"
                className="input input-bordered w-full text-sm"
                placeholder="https://..."
                {...register("url")}
              />
              {errors.url && (
                <span className="text-xs text-error mt-1 block">{errors.url.message}</span>
              )}
            </div>
            <label className="cursor-pointer flex items-start gap-3 p-3 rounded-md border border-base-300/60 bg-base-200/30">
              <input
                type="checkbox"
                className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
                {...register("hasPreview")}
              />
              <div className="text-left min-w-0 flex-1">
                <div className="text-sm font-medium">Tiene vista previa</div>
                <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
                  Cuando está activo, aparece el botón "Ver" en la lista que abre el link en un
                  iframe sin salir del panel. Desactívalo para sitios que bloquean iframes (GitHub,
                  GitLab, etc.) — quedan solo como link externo.
                </div>
              </div>
            </label>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
                {create.isPending && <span className="loading loading-spinner loading-xs" />}
                Agregar
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>cerrar</button>
        </form>
      </dialog>
    );
  },
);

// ─── Edit ─────────────────────────────────────────────────────────────────

function EditLinkDialog({
  projectId,
  link,
  onClose,
}: {
  projectId: string;
  link: ProjectLink;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(projectLinkCreateSchema),
    defaultValues: {
      kind: link.kind,
      label: link.label,
      url: link.url,
      hasPreview: link.hasPreview,
    },
  });

  const update = useMutation({
    mutationFn: (input: { kind: string; label: string; url: string; hasPreview: boolean }) =>
      api.patch(`/projects/${projectId}/links/${link.id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      onClose();
    },
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box card-elevated">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-display font-semibold">Editar link</h3>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit((d) => update.mutate(d))} className="space-y-3">
          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Tipo</span>
            </label>
            <select className="select select-bordered w-full" {...register("kind")}>
              {PROJECT_LINK_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_META[k].label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">Etiqueta</span>
            </label>
            <input className="input input-bordered w-full" autoFocus {...register("label")} />
            {errors.label && (
              <span className="text-xs text-error mt-1 block">{errors.label.message}</span>
            )}
          </div>
          <div>
            <label className="label py-1">
              <span className="label-text text-sm font-medium">URL</span>
            </label>
            <input
              type="url"
              className="input input-bordered w-full text-sm"
              {...register("url")}
            />
            {errors.url && (
              <span className="text-xs text-error mt-1 block">{errors.url.message}</span>
            )}
          </div>
          <label className="cursor-pointer flex items-start gap-3 p-3 rounded-md border border-base-300/60 bg-base-200/30">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
              {...register("hasPreview")}
            />
            <div className="text-left min-w-0 flex-1">
              <div className="text-sm font-medium">Tiene vista previa</div>
              <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
                Cuando está activo aparece el botón "Ver" que abre el link en un iframe. Desactívalo
                para sitios que bloquean iframes.
              </div>
            </div>
          </label>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={update.isPending || !isDirty}
            >
              {update.isPending && <span className="loading loading-spinner loading-xs" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete confirm ───────────────────────────────────────────────────────

const DeleteLinkDialog = forwardRef<
  HTMLDialogElement,
  { projectId: string; link: ProjectLink | null; onClose: () => void }
>(function DeleteLinkDialog({ projectId, link, onClose }, ref) {
  const qc = useQueryClient();

  const remove = useMutation({
    mutationFn: () => api.del(`/projects/${projectId}/links/${link!.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      onClose();
    },
  });

  return (
    <dialog ref={ref} className="modal">
      <div className="modal-box card-elevated max-w-sm">
        <h3 className="text-lg font-display font-semibold mb-2">Eliminar link</h3>
        <p className="text-sm text-base-content/70 mb-4">
          ¿Eliminar <span className="font-medium">{link?.label}</span>? No se puede deshacer.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-error btn-sm"
            onClick={() => remove.mutate()}
            disabled={remove.isPending || !link}
          >
            {remove.isPending && <span className="loading loading-spinner loading-xs" />}
            Eliminar
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button>cerrar</button>
      </form>
    </dialog>
  );
});
